import React, { useEffect, useState } from 'react';
import { shipmentAPI, warehouseAPI } from '../services/api';
import { getShipmentStatusBadge, formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';

function CreateShipmentModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ departurePort: 'CHINA_PORT', shipmentMode: 'SEA', estimatedDeparture: '', estimatedArrival: '', notes: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await shipmentAPI.create(form);
      toast.success('Shipment created!');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Create Shipment</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Departure Port *</label>
              <select className="form-select" value={form.departurePort} onChange={e => setForm({ ...form, departurePort: e.target.value })} required>
                <option value="CHINA_PORT">China Port</option>
                <option value="HONGKONG_PORT">Hong Kong Port</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Shipment Mode *</label>
              <select className="form-select" value={form.shipmentMode} onChange={e => setForm({ ...form, shipmentMode: e.target.value })} required>
                <option value="SEA">Sea Freight</option>
                <option value="AIR">Air Freight</option>
                <option value="SEA_AIR">Sea + Air</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Est. Departure</label>
              <input className="form-input" type="date" value={form.estimatedDeparture} onChange={e => setForm({ ...form, estimatedDeparture: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Est. Arrival</label>
              <input className="form-input" type="date" value={form.estimatedArrival} onChange={e => setForm({ ...form, estimatedArrival: e.target.value })} />
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Notes</label>
              <textarea className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ resize: 'vertical' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
              Create Shipment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddCartonsModal({ shipment, onClose, onSaved }) {
  const [cartons, setCartons] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    warehouseAPI.getAll().then(r => {
      const eligible = (r.data.data || []).filter(c => c.barcode && ['REGISTERED','SORTING'].includes(c.status));
      setCartons(eligible);
    });
  }, []);

  const toggle = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleAdd = async () => {
    if (!selected.length) return toast.error('Select at least one carton');
    setLoading(true);
    try {
      await shipmentAPI.addCartons(shipment.id, { cartonIds: selected });
      toast.success(`${selected.length} carton(s) added to shipment!`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Add Cartons to {shipment.shipmentNumber}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Showing cartons with barcode in REGISTERED or SORTING status</p>
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
          {cartons.length === 0 ? (
            <div className="empty-state"><div className="empty-state-text">No eligible cartons available</div></div>
          ) : cartons.map(c => (
            <label key={c.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 16px', cursor: 'pointer',
              borderBottom: '1px solid var(--border)',
              background: selected.includes(c.id) ? 'rgba(59,130,246,0.08)' : 'transparent',
              transition: 'background 0.15s',
            }}>
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} style={{ accentColor: 'var(--accent)', width: 16, height: 16 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>{c.grnNumber}</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 2 }}>{c.productDescription || 'No description'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{c.weightKg} kg | CBM: {c.cbm}</div>
              </div>
              <span className="badge badge-blue" style={{ fontSize: 10 }}>{c.status}</span>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{selected.length} carton(s) selected</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAdd} disabled={loading || !selected.length}>
              {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : null}
              Add to Shipment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DispatchModal({ shipment, onClose, onSaved }) {
  const [form, setForm] = useState({ vesselName: '', blNumber: '', actualDeparture: '', estimatedArrival: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await shipmentAPI.dispatch(shipment.id, form);
      toast.success('Shipment dispatched!');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Dispatch Shipment</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Vessel / Flight Name</label>
            <input className="form-input" value={form.vesselName} onChange={e => setForm({ ...form, vesselName: e.target.value })} placeholder="COSCO SHIPPING ARIES" />
          </div>
          <div className="form-group">
            <label className="form-label">BL / AWB Number</label>
            <input className="form-input" value={form.blNumber} onChange={e => setForm({ ...form, blNumber: e.target.value })} placeholder="COSU1234567890" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Actual Departure</label>
              <input className="form-input" type="date" value={form.actualDeparture} onChange={e => setForm({ ...form, actualDeparture: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Est. Arrival BD</label>
              <input className="form-input" type="date" value={form.estimatedArrival} onChange={e => setForm({ ...form, estimatedArrival: e.target.value })} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" style={{ width: 14, height: 14 }} /> : '⬡'}
              Dispatch
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Shipments() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    shipmentAPI.getAll()
      .then(res => setShipments(res.data.data || []))
      .catch(() => toast.error('Failed to load shipments'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleMarkInTransit = async (id) => {
    const location = window.prompt('Enter current location (optional):', 'South China Sea');
    if (location === null) return;
    try {
      await shipmentAPI.markInTransit(id, location || '');
      toast.success('Shipment marked as in transit!');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleMarkArrived = async (id) => {
    if (!window.confirm('Mark this shipment as arrived in Bangladesh?')) return;
    try {
      await shipmentAPI.markArrived(id);
      toast.success('Shipment marked as arrived!');
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="animate-fade">
      <div className="page-header">
        <h1 className="page-title">Shipments</h1>
        <p className="page-subtitle">{shipments.length} total shipments</p>
      </div>

      <div className="action-bar">
        <div />
        <button className="btn btn-primary" onClick={() => setModal('create')}>+ New Shipment</button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Shipment No.</th>
              <th>Port</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Vessel / BL</th>
              <th>Cartons</th>
              <th>Weight</th>
              <th>Est. Departure</th>
              <th>Est. Arrival</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40 }}><span className="spinner" /></td></tr>
            ) : shipments.length === 0 ? (
              <tr><td colSpan={10}><div className="empty-state"><div className="empty-state-icon">⬡</div><div className="empty-state-text">No shipments yet</div></div></td></tr>
            ) : shipments.map(s => (
              <tr key={s.id}>
                <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)' }}>{s.shipmentNumber}</span></td>
                <td><span className="badge badge-blue" style={{ fontSize: 10 }}>{s.departurePort === 'CHINA_PORT' ? 'China' : 'HK'}</span></td>
                <td><span className="badge badge-cyan" style={{ fontSize: 10 }}>{s.shipmentMode}</span></td>
                <td><span className={`badge ${getShipmentStatusBadge(s.status)}`}>{s.status}</span></td>
                <td style={{ fontSize: 12 }}>
                  {s.vesselName ? <div style={{ color: 'var(--text-primary)' }}>{s.vesselName}</div> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  {s.blNumber && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{s.blNumber}</div>}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{s.totalCartons || 0}</td>
                <td>{s.totalWeight ? `${s.totalWeight} kg` : '—'}</td>
                <td style={{ fontSize: 12 }}>{formatDate(s.estimatedDeparture)}</td>
                <td style={{ fontSize: 12 }}>{formatDate(s.estimatedArrival)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['CREATED','PACKING'].includes(s.status) && (
                      <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(s); setModal('addCartons'); }}>+ Cartons</button>
                    )}
                    {['CREATED','PACKING','READY'].includes(s.status) && (
                      <button className="btn btn-primary btn-sm" onClick={() => { setSelected(s); setModal('dispatch'); }}>Dispatch</button>
                    )}
                    {s.status === 'DEPARTED' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => handleMarkInTransit(s.id)}>In Transit</button>
                    )}
                    {['DEPARTED','IN_TRANSIT'].includes(s.status) && (
                      <button className="btn btn-success btn-sm" onClick={() => handleMarkArrived(s.id)}>Mark Arrived</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal === 'create' && <CreateShipmentModal onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {modal === 'addCartons' && selected && <AddCartonsModal shipment={selected} onClose={() => { setModal(null); setSelected(null); }} onSaved={() => { setModal(null); setSelected(null); load(); }} />}
      {modal === 'dispatch' && selected && <DispatchModal shipment={selected} onClose={() => { setModal(null); setSelected(null); }} onSaved={() => { setModal(null); setSelected(null); load(); }} />}
    </div>
  );
}
